import { AppShell } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
	createRootRoute,
	Outlet,
	useRouterState,
} from "@tanstack/react-router";
import { useEffect } from "react";
import { ErrorBoundary } from "../components/ErrorBoundary";
import { Header } from "../components/Header/Header";
import { SearchProvider } from "../contexts/SearchContext";
import { ViewStateProvider } from "../contexts/ViewStateContext";
import {
	BetaAccessStatus,
	useGetCurrentUserAndOrganizationsQuery,
} from "../graphql/generated/types.gen";
import { useActiveOrganization } from "../hooks/useActiveOrganization";
import { useAuthCompat } from "../hooks/useAuthCompat";
import { useIsMobile } from "../hooks/useIsMobile";
import { PermissionsProvider } from "../permissions/PermissionsProvider";

const MINIMAL_PAGES = ["/organization-setup", "/waitlist", "/welcome"];
const HEADER_HEIGHT_DEFAULT = 80;
const HEADER_HEIGHT_MINIMAL = 60;
const HEADER_HEIGHT_WITH_SEARCH = 96; // controls (~52px) + searchbar (~40px) + buffer

function RootLayout() {
	const routerState = useRouterState();
	const isMobile = useIsMobile();
	const isNarrowScreen = useMediaQuery("(max-width: 640px)") ?? false;
	const { pathname } = routerState.location;
	const isMinimalPage =
		MINIMAL_PAGES.includes(pathname) || pathname.startsWith("/invite/");

	const { user } = useAuthCompat();
	const { activeOrganization } = useActiveOrganization();
	const { data: userData } = useGetCurrentUserAndOrganizationsQuery({
		skip: !user,
	});
	const viewScope =
		user?.id && activeOrganization
			? { userId: user.id, organizationId: activeOrganization.id }
			: null;

	const isAuthenticated = user && userData?.currentUser;
	const isWaitlisted =
		userData?.currentUser?.betaAccessStatus === BetaAccessStatus.Waitlist;
	const hasFullAccess = isAuthenticated && !isWaitlisted;

	// Determine header height based on whether searchbar is stacked
	const showStackedSearchbar =
		isMobile && isNarrowScreen && hasFullAccess && !isMinimalPage;

	let headerHeight = HEADER_HEIGHT_DEFAULT;
	if (isMobile && isMinimalPage) {
		headerHeight = HEADER_HEIGHT_MINIMAL;
	} else if (showStackedSearchbar) {
		headerHeight = HEADER_HEIGHT_WITH_SEARCH;
	}

	// Sync CSS variable with computed header height
	useEffect(() => {
		document.documentElement.style.setProperty(
			"--header-height",
			`${headerHeight}px`,
		);
	}, [headerHeight]);

	return (
		<ErrorBoundary>
			<ViewStateProvider scope={viewScope}>
				<PermissionsProvider>
					<SearchProvider>
						<AppShell
							header={{ height: headerHeight }}
							padding={0}
							withBorder={false}
						>
							<AppShell.Header>
								<Header />
							</AppShell.Header>
							<AppShell.Main>
								<Outlet />
							</AppShell.Main>
						</AppShell>
					</SearchProvider>
				</PermissionsProvider>
			</ViewStateProvider>
		</ErrorBoundary>
	);
}

export const Route = createRootRoute({
	component: RootLayout,
});
